import React, { useEffect, useRef, useState } from 'react';
import {
  Search,
  TrendingUp,
  Sparkles,
  Users,
  Calendar,
  Unlock,
  ExternalLink,
  Bookmark,
  Eye,
  Star,
  Zap,
  Filter,
  BookOpen,
} from 'lucide-react';
import './PapersShowcase.css';

/**
 * PapersShowcase - Research Papers Feature Section
 * 3 distinct slide types: Search Results, Trending, Recommendations
 * Cursor-landing-page inspired design
 */
const PapersShowcase = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [typingComplete, setTypingComplete] = useState(false);

  // Dynamic animation states
  const [visibleItems, setVisibleItems] = useState(0);
  const [counterProgress, setCounterProgress] = useState(0);
  const [liveOffsets, setLiveOffsets] = useState({ search: [0, 0, 0], trending: [0, 0, 0, 0] });
  const [flashIndex, setFlashIndex] = useState(null);
  const liveTimerRef = useRef(null);

  // Intersection observer for visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-rotate slides (resets on manual slide change)
  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => {
      setActiveSlide((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isVisible, activeSlide]);

  // Typing animation for search
  useEffect(() => {
    if (!isVisible || activeSlide !== 0) {
      setSearchText('');
      setTypingComplete(false);
      return;
    }

    const searchQuery = 'transformer attention mechanisms';
    let index = 0;
    setSearchText('');
    setTypingComplete(false);

    const typeInterval = setInterval(() => {
      if (index < searchQuery.length) {
        setSearchText(searchQuery.slice(0, index + 1));
        index++;
      } else {
        setTypingComplete(true);
        clearInterval(typeInterval);
      }
    }, 60);

    return () => clearInterval(typeInterval);
  }, [isVisible, activeSlide]);

  // Stagger reveal + counter animation on slide change
  useEffect(() => {
    if (!isVisible) return;

    setVisibleItems(0);
    setCounterProgress(0);
    setFlashIndex(null);

    const itemCounts = [3, 4, 3];
    const maxItems = itemCounts[activeSlide];
    const timers = [];

    for (let i = 0; i < maxItems; i++) {
      timers.push(setTimeout(() => setVisibleItems(i + 1), 200 + i * 220));
    }

    let step = 0;
    const totalSteps = 30;
    const counterTimer = setInterval(() => {
      step++;
      const t = step / totalSteps;
      setCounterProgress(1 - Math.pow(1 - t, 3));
      if (step >= totalSteps) clearInterval(counterTimer);
    }, 33);

    return () => {
      timers.forEach(t => { clearTimeout(t); clearInterval(t); });
      clearInterval(counterTimer);
    };
  }, [activeSlide, isVisible]);

  // Live micro-updates (citations tick up periodically)
  useEffect(() => {
    if (!isVisible) return;

    const startDelay = setTimeout(() => {
      liveTimerRef.current = setInterval(() => {
        if (activeSlide === 0) {
          const idx = Math.floor(Math.random() * 3);
          const bump = Math.floor(Math.random() * 8) + 1;
          setLiveOffsets(prev => {
            const arr = [...prev.search];
            arr[idx] += bump;
            return { ...prev, search: arr };
          });
          setFlashIndex(idx);
          setTimeout(() => setFlashIndex(null), 700);
        } else if (activeSlide === 1) {
          const idx = Math.floor(Math.random() * 4);
          const bump = Math.floor(Math.random() * 5) + 1;
          setLiveOffsets(prev => {
            const arr = [...prev.trending];
            arr[idx] += bump;
            return { ...prev, trending: arr };
          });
          setFlashIndex(idx);
          setTimeout(() => setFlashIndex(null), 700);
        }
      }, 3000);
    }, 2000);

    return () => {
      clearTimeout(startDelay);
      if (liveTimerRef.current) clearInterval(liveTimerRef.current);
    };
  }, [activeSlide, isVisible]);

  // Animation helpers
  const animateCount = (target) => Math.floor(target * counterProgress);
  const formatViews = (num) => {
    if (num >= 1000) return Math.floor(num / 1000).toLocaleString() + 'K';
    return num.toLocaleString();
  };

  // ========================================
  // SLIDE 1: Search Results Data
  // ========================================
  const searchResults = [
    {
      title: 'Attention Is All You Need',
      authors: ['A. Vaswani', 'N. Shazeer', 'et al.'],
      year: 2017,
      journal: 'NeurIPS',
      citations: 98420,
      openAccess: true,
      trending: true,
      score: 9.8,
      domains: ['Computer Science', 'AI'],
      abstract: 'We propose a new simple network architecture, the Transformer, based solely on attention mechanisms...',
    },
    {
      title: 'BERT: Pre-training of Deep Bidirectional Transformers',
      authors: ['J. Devlin', 'M. Chang', 'et al.'],
      year: 2019,
      journal: 'NAACL',
      citations: 72850,
      openAccess: true,
      score: 9.6,
      domains: ['NLP', 'Machine Learning'],
      abstract: 'We introduce BERT, designed to pre-train deep bidirectional representations from unlabeled text...',
    },
    {
      title: 'An Image is Worth 16x16 Words: Transformers for Image Recognition',
      authors: ['A. Dosovitskiy', 'L. Beyer', 'et al.'],
      year: 2021,
      journal: 'ICLR',
      citations: 28340,
      openAccess: true,
      recommended: true,
      score: 9.4,
      domains: ['Computer Vision', 'Deep Learning'],
      abstract: 'We show that a pure transformer applied directly to sequences of image patches can perform very well...',
    },
  ];

  // ========================================
  // SLIDE 2: Trending Papers Data
  // ========================================
  const trendingPapers = [
    {
      title: 'GPT-4 Technical Report',
      authors: ['OpenAI'],
      year: 2024,
      citations: 4250,
      views: 125000,
      trendScore: 98,
      field: 'Artificial Intelligence',
      fieldColor: '#f97316',
      growth: '+340%',
    },
    {
      title: 'Constitutional AI: Harmlessness from AI Feedback',
      authors: ['Y. Bai', 'S. Kadavath', 'et al.'],
      year: 2024,
      citations: 1890,
      views: 89000,
      trendScore: 94,
      field: 'AI Safety',
      fieldColor: '#ef4444',
      growth: '+280%',
    },
    {
      title: 'Scaling Laws for Neural Language Models',
      authors: ['J. Kaplan', 'et al.'],
      year: 2024,
      citations: 2340,
      views: 67000,
      trendScore: 91,
      field: 'Machine Learning',
      fieldColor: '#f59e0b',
      growth: '+195%',
    },
    {
      title: 'Chain-of-Thought Prompting Elicits Reasoning',
      authors: ['J. Wei', 'X. Wang', 'et al.'],
      year: 2024,
      citations: 1560,
      views: 54000,
      trendScore: 87,
      field: 'NLP',
      fieldColor: '#ec4899',
      growth: '+156%',
    },
  ];

  // ========================================
  // SLIDE 3: Recommended Papers Data
  // ========================================
  const recommendedPapers = [
    {
      title: 'Retrieval-Augmented Generation for Knowledge-Intensive NLP Tasks',
      authors: ['P. Lewis', 'E. Perez', 'et al.'],
      year: 2023,
      reason: 'Based on your interest in transformers',
      match: 96,
      citations: 3420,
      tags: ['RAG', 'NLP', 'LLMs'],
    },
    {
      title: 'Flash Attention: Fast and Memory-Efficient Exact Attention',
      authors: ['T. Dao', 'D. Fu', 'et al.'],
      year: 2023,
      reason: 'Similar to papers you\'ve saved',
      match: 94,
      citations: 2180,
      tags: ['Optimization', 'Transformers'],
    },
    {
      title: 'LLaMA: Open and Efficient Foundation Language Models',
      authors: ['H. Touvron', 'T. Lavril', 'et al.'],
      year: 2023,
      reason: 'Trending in your field',
      match: 91,
      citations: 5670,
      tags: ['LLMs', 'Open Source'],
    },
  ];

  // ========================================
  // RENDER: Slide 1 - Search Results
  // ========================================
  const renderSearchResults = () => (
    <div className="pshow__slide pshow__slide--search">
      {/* Search Header */}
      <div className="pshow__search-header">
        <div className="pshow__search-box">
          <Search size={14} className="pshow__search-icon" />
          <span className="pshow__search-text">
            {searchText}
            <span className={`pshow__cursor ${typingComplete ? 'pshow__cursor--blink' : ''}`}>|</span>
          </span>
        </div>
        <div className="pshow__search-filters">
          <span className="pshow__filter-chip pshow__filter-chip--active">
            <Filter size={10} />
            All Fields
          </span>
          <span className="pshow__filter-chip">2020-2024</span>
          <span className="pshow__filter-chip">
            <Unlock size={10} />
            Open Access
          </span>
        </div>
      </div>

      {/* Results Count */}
      <div className="pshow__results-meta">
        <span className="pshow__results-count">
          <strong>{animateCount(12847).toLocaleString()}</strong> results found
        </span>
        <span className="pshow__results-sort">Sort: Relevance</span>
      </div>

      {/* Paper Cards */}
      <div className="pshow__papers-list">
        {searchResults.slice(0, visibleItems).map((paper, i) => (
          <div 
            key={i} 
            className={`pshow__paper-card${flashIndex === i && activeSlide === 0 ? ' pshow__card--flash' : ''}`}
          >
            <div className="pshow__paper-header">
              <h4 className="pshow__paper-title">{paper.title}</h4>
              <div className="pshow__paper-badges">
                {paper.openAccess && (
                  <span className="pshow__badge pshow__badge--open">
                    <Unlock size={9} /> Open
                  </span>
                )}
                {paper.trending && (
                  <span className="pshow__badge pshow__badge--trending">
                    <TrendingUp size={9} />
                  </span>
                )}
                {paper.recommended && (
                  <span className="pshow__badge pshow__badge--rec">
                    <Zap size={9} />
                  </span>
                )}
                {paper.score && (
                  <span className="pshow__badge pshow__badge--score">
                    <Star size={9} /> {paper.score}
                  </span>
                )}
              </div>
            </div>
            <div className="pshow__paper-meta">
              <span><Users size={10} /> {paper.authors.join(', ')}</span>
              <span className="pshow__meta-dot">•</span>
              <span><Calendar size={10} /> {paper.year}</span>
              <span className="pshow__meta-dot">•</span>
              <span>{paper.journal}</span>
            </div>
            <p className="pshow__paper-abstract">{paper.abstract}</p>
            <div className="pshow__paper-footer">
              <div className="pshow__paper-tags">
                {paper.domains.map((d, j) => (
                  <span key={j} className="pshow__tag">{d}</span>
                ))}
              </div>
              <div className="pshow__paper-stats">
                <span className={`pshow__stat${flashIndex === i && activeSlide === 0 ? ' pshow__stat--updated' : ''}`}>
                  <BookOpen size={10} /> {animateCount(paper.citations + (liveOffsets.search[i] || 0)).toLocaleString()}
                </span>
                <button className="pshow__action-btn">
                  <Bookmark size={12} />
                </button>
                <button className="pshow__action-btn">
                  <ExternalLink size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ========================================
  // RENDER: Slide 2 - Trending Papers
  // ========================================
  const renderTrendingPapers = () => (
    <div className="pshow__slide pshow__slide--trending">
      {/* Header */}
      <div className="pshow__trending-header">
        <div className="pshow__trending-title">
          <TrendingUp size={16} className="pshow__trending-icon" />
          <span>Trending This Week</span>
        </div>
        <div className="pshow__trending-tabs">
          <button className="pshow__trend-tab pshow__trend-tab--active">Week</button>
          <button className="pshow__trend-tab">Month</button>
          <button className="pshow__trend-tab">Year</button>
        </div>
      </div>

      {/* Trending List */}
      <div className="pshow__trending-list">
        {trendingPapers.slice(0, visibleItems).map((paper, i) => (
          <div 
            key={i} 
            className={`pshow__trend-card${flashIndex === i && activeSlide === 1 ? ' pshow__card--flash' : ''}`}
          >
            <div className="pshow__trend-rank">
              <span className="pshow__rank-num">{i + 1}</span>
              <div className="pshow__trend-score-bar">
                <div 
                  className="pshow__trend-score-fill"
                  style={{ width: `${animateCount(paper.trendScore)}%` }}
                />
              </div>
            </div>
            <div className="pshow__trend-content">
              <h4 className="pshow__trend-title">{paper.title}</h4>
              <div className="pshow__trend-meta">
                <span className="pshow__trend-field" style={{ color: paper.fieldColor, borderColor: paper.fieldColor }}>
                  {paper.field}
                </span>
                <span className="pshow__trend-authors">{paper.authors.join(', ')}</span>
              </div>
            </div>
            <div className="pshow__trend-right">
              <div className="pshow__trend-stats-grid">
                <div className="pshow__trend-stat-item">
                  <span className="pshow__trend-stat-value">
                    {animateCount(paper.citations + (liveOffsets.trending[i] || 0)).toLocaleString()}
                  </span>
                  <span className="pshow__trend-stat-label">Citations</span>
                </div>
                <div className="pshow__trend-stat-item">
                  <span className="pshow__trend-stat-value">
                    {formatViews(animateCount(paper.views))}
                  </span>
                  <span className="pshow__trend-stat-label">Views</span>
                </div>
              </div>
              <span className="pshow__trend-growth-badge">{paper.growth}</span>
              <div className="pshow__trend-actions">
                <button className="pshow__action-btn"><Bookmark size={12} /></button>
                <button className="pshow__action-btn"><ExternalLink size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ========================================
  // RENDER: Slide 3 - Recommended Papers
  // ========================================
  const renderRecommendedPapers = () => (
    <div className="pshow__slide pshow__slide--recommended">
      {/* Header */}
      <div className="pshow__rec-header">
        <div className="pshow__rec-title">
          <Sparkles size={16} className="pshow__rec-icon" />
          <span>Recommended for You</span>
        </div>
        <span className="pshow__rec-subtitle">Personalized based on your research interests</span>
      </div>

      {/* Recommended List */}
      <div className="pshow__rec-list">
        {recommendedPapers.slice(0, visibleItems).map((paper, i) => (
          <div 
            key={i} 
            className="pshow__rec-card"
          >
            <div className="pshow__rec-match">
              <div className="pshow__match-circle">
                <svg viewBox="0 0 36 36" className="pshow__match-svg">
                  <path
                    className="pshow__match-bg"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="pshow__match-fill"
                    strokeDasharray={`${animateCount(paper.match)}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <span className="pshow__match-text">{animateCount(paper.match)}%</span>
              </div>
            </div>
            <div className="pshow__rec-content">
              <h4 className="pshow__rec-paper-title">{paper.title}</h4>
              <span className="pshow__rec-authors">
                <Users size={10} /> {paper.authors.join(', ')} • {paper.year}
              </span>
              <div className="pshow__rec-reason">
                <Zap size={10} className="pshow__reason-icon" />
                {paper.reason}
              </div>
            </div>
            <div className="pshow__rec-right-col">
              <div className="pshow__rec-tags">
                {paper.tags.map((tag, j) => (
                  <span key={j} className="pshow__rec-tag">{tag}</span>
                ))}
              </div>
              <div className="pshow__rec-stat-row">
                <span className="pshow__rec-citations"><BookOpen size={10} /> {animateCount(paper.citations).toLocaleString()} citations</span>
              </div>
              <div className="pshow__rec-actions">
                <button className="pshow__rec-btn pshow__rec-btn--save">
                  <Bookmark size={14} />
                </button>
                <button className="pshow__rec-btn">
                  <ExternalLink size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* AI Insight */}
      <div className="pshow__ai-insight">
        <Sparkles size={12} className="pshow__ai-icon" />
        <span>AI analyzes your reading patterns to surface relevant research</span>
      </div>
    </div>
  );

  // Slides array
  const slides = [
    { render: renderSearchResults, label: 'Search' },
    { render: renderTrendingPapers, label: 'Trending' },
    { render: renderRecommendedPapers, label: 'For You' },
  ];

  return (
    <section
      ref={sectionRef}
      className={`pshow ${isVisible ? 'pshow--visible' : ''}`}
    >
      <div className="pshow__container">
        {/* Top - Text Content */}
        <div className="pshow__header">
          <div className="pshow__text">
            <h2 className="pshow__title">200M+ papers at your fingertips</h2>
            <p className="pshow__desc">
              Search the world's largest research database. Discover trending papers, get personalized recommendations, and build your library.
            </p>
          </div>
          <a href="/explore" className="pshow__link">
            Explore Papers <span className="pshow__arrow">→</span>
          </a>
        </div>

        {/* Bottom - Full Width Rotating Slides */}
        <div className="pshow__cards">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`pshow__card ${index === activeSlide ? 'pshow__card--active' : ''} ${
                index < activeSlide ? 'pshow__card--prev' : ''
              } ${index > activeSlide ? 'pshow__card--next' : ''}`}
            >
              {slide.render()}
            </div>
          ))}

          {/* Slide Indicators */}
          <div className="pshow__indicators">
            {slides.map((slide, i) => (
              <button
                key={i}
                className={`pshow__indicator ${i === activeSlide ? 'pshow__indicator--active' : ''}`}
                onClick={() => setActiveSlide(i)}
              >
                {i === activeSlide && (
                  <span className="pshow__indicator-progress" key={`prog-${activeSlide}`} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PapersShowcase;
